import React, { useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ItemContent from '../../components/ItemContent/ItemContent';
import { IEditForm } from '../../types/interfaces/IEditForm';
import { NSCruiseShips } from '../../types/interfaces/NSCruiseShips';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { EPaths } from '../../types/enums/EPaths';
import { SessionContext } from '../../contexts/session/SessionContext';
import { endpoints } from '../../utils/endpoints';
import useApi from '../../utils/useApi';
import { NSPackages } from '../../types/interfaces/NSPackages';
import { EStatus } from '../../types/enums/EStatus';
import { ETables } from '../../types/enums/ETables';
import { ITableData } from '../../types/interfaces/ITableData';

const EditCruiseShip = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { permissions } = useContext(SessionContext);
  const { data: cruiseLinesData } = useApi<ITableData>(endpoints.table({ tableName: ETables.CruiseLines, page: 1 }));
  const { data: packagesData } = useApi<ITableData>(endpoints.table({ tableName: ETables.Packages, page: 1 }));

  const cruiseLines = cruiseLinesData?.table || [];
  const packagesList = (packagesData?.table || []) as NSPackages.ITableItem[];

  const cruiseLineOptions = useMemo(
    () =>
      cruiseLines.map((line: any) => ({
        value: line._id || line.id,
        label: line.name,
      })),
    [cruiseLines],
  );

  const packageOptions = useMemo(
    () =>
      packagesList?.map((item) => ({
        value: item._id,
        label: `${item.title} (${item.label})`,
      })) || [],
    [packagesList],
  );

  const contentMapper = useCallback(
    (data: NSCruiseShips.ICruiseShipDetails): IEditForm[][] => {
      const imageUrl = data.image?.en || Object.values(data.image || {})[0];

      return [
        [
          {
            type: 'image',
            fieldName: 'image',
            label: t(tPaths.editFormLabels.image),
            initialValue: imageUrl || null,
          },
          {
            type: 'input',
            fieldName: 'name',
            label: t(tPaths.editFormLabels.name),
            initialValue: data.name?.en || Object.values(data.name || {})[0] || '',
          },
          {
            type: 'select',
            fieldName: 'cruiseLineIds',
            label: t(tPaths.editFormLabels.cruiseLine),
            variants: cruiseLineOptions,
            initialValue: data.cruiseLines?.map((line) => line._id || (line as any).id) || [],
            multiple: true,
          },
          {
            type: 'select',
            fieldName: 'packageIds',
            label: t(tPaths.editFormLabels.plans),
            variants: packageOptions,
            initialValue: data.packages?.map((pkg) => pkg._id || (pkg as any).id) || [],
            multiple: true,
          },
        ],
      ];
    },
    [cruiseLineOptions, packageOptions, t],
  );

  const updateDataFormatter = useCallback((fields: any) => {
    const rawCruiseLineIds = fields.cruiseLineIds?.value;
    const rawPackageIds = fields.packageIds?.value;
    const imageValue = fields.image?.value;
    const imageUrl = typeof imageValue === 'string' ? imageValue : imageValue?.url;

    return {
      name: { en: fields.name?.value?.trim() },
      image: imageUrl ? { en: imageUrl } : {},
      cruiseLineIds: Array.isArray(rawCruiseLineIds) ? rawCruiseLineIds : rawCruiseLineIds ? [rawCruiseLineIds] : [],
      packageIds: Array.isArray(rawPackageIds) ? rawPackageIds : rawPackageIds ? [rawPackageIds] : [],
      status: fields.status ? 'Active' : 'Inactive',
    };
  }, []);

  const switches = useMemo(() => [{ key: 'status', label: t(tPaths.itemContent.active) }], [t]);

  const switchesDataMapper = useCallback((data: NSCruiseShips.ICruiseShipDetails) => {
    const current = data.status?.current;
    return { status: current === 'Active' || current === EStatus.Active };
  }, []);

  return (
    <ItemContent
      itemId={id}
      title={t(tPaths.itemContent.editCruiseShip)}
      path={EPaths.CruiseShips}
      contentMapper={contentMapper}
      updateDataFormatter={updateDataFormatter}
      switches={switches}
      switchesDataMapper={switchesDataMapper}
      editable={permissions.packages.edit}
      apiUrl={endpoints.cruiseShip(id)}
    />
  );
};

export default EditCruiseShip;
